import { api } from '@/lib/api/client';
import type {
  User,
  UserFilters,
  UsersResponse,
  CreateUserDto,
  UpdateUserDto,
  ChangeOwnPasswordDto,
  ChangeUserPasswordDto,
} from '../types';

const USERS_URL = '/users';

export const usersApi = {
  // Get all users with filters
  getAll: async (filters?: UserFilters): Promise<UsersResponse> => {
    const params: Record<string, unknown> = { ...filters };

    if (params['page'] !== undefined && params['limit']) {
      params['offset'] = (params['page'] as number) * (params['limit'] as number);
      delete params['page'];
    }

    // Handle search - only send if 2+ chars
    if (params['s'] && (params['s'] as string).length < 2) {
      delete params['s'];
    }

    // Handle date filters
    if (params['createdAt.min']) {
      const minDate = new Date(params['createdAt.min'] as string);
      minDate.setHours(0, 0, 0, 0);
      params['createdAt.min'] = minDate.toISOString();
    }

    if (params['createdAt.max']) {
      const maxDate = new Date(params['createdAt.max'] as string);
      maxDate.setHours(23, 59, 59, 999);
      params['createdAt.max'] = maxDate.toISOString();
    }

    // Remove empty values
    Object.keys(params).forEach((key) => {
      if (params[key] == null || params[key] === '') {
        delete params[key];
      }
    });

    // Remove order params if empty
    if (!params['orderBy'] || !params['order']) {
      delete params['orderBy'];
      delete params['order'];
    }

    return api.get<UsersResponse>(USERS_URL, { params });
  },

  // Get single user by ID
  getById: async (id: number | string): Promise<User> => {
    return api.get<User>(`${USERS_URL}/${id}`);
  },

  // Create new user
  create: async (data: CreateUserDto): Promise<User> => {
    return api.post<User>(USERS_URL, data);
  },

  // Update user
  update: async ({ id, data }: { id: number | string; data: UpdateUserDto }): Promise<User> => {
    return api.patch<User>(`${USERS_URL}/${id}`, data);
  },

  // Delete user
  delete: async (id: number | string): Promise<void> => {
    return api.delete(`${USERS_URL}/${id}`);
  },

  // Change own password
  changeOwnPassword: async (data: ChangeOwnPasswordDto): Promise<void> => {
    return api.patch(`${USERS_URL}/change-password`, data);
  },

  // Change another user's password
  changeUserPassword: async ({ id, data }: { id: number | string; data: ChangeUserPasswordDto }): Promise<void> => {
    return api.patch(`${USERS_URL}/${id}/change-password`, data);
  },

  // Activate user
  activate: async (id: number | string): Promise<User> => {
    return api.put<User>(`${USERS_URL}/activate/${id}`, {});
  },

  // Block user
  block: async (id: number | string): Promise<User> => {
    return api.put<User>(`${USERS_URL}/block/${id}`, {});
  },
};

// Query keys for React Query
export const usersQueryKeys = {
  all: ['users'] as const,
  lists: () => [...usersQueryKeys.all, 'list'] as const,
  list: (filters?: UserFilters) => [...usersQueryKeys.lists(), filters] as const,
  details: () => [...usersQueryKeys.all, 'detail'] as const,
  detail: (id: number | string) => [...usersQueryKeys.details(), id] as const,
};
